import { Badge, Box, Typography, useTheme } from '@mui/material'
import { navigate } from 'gatsby'
import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { MODAL_WIDTH_MEDIUM } from '../../../constants/sizes'
import { CircleButton, HBox, VBox, WhiteBox } from '../../../elements/basic-elements'
import useSearchModal from '../../../hooks/useSearchModal'
import FaqIcon from '../../../images/icons/faq-icon'
import LogoutIcon from '../../../images/icons/logout-icon'
import MagGlassIcon from '../../../images/icons/mag-glass-icon'
import NewsletterIcon from '../../../images/icons/newsletter-icon'
import PodcastIcon from '../../../images/icons/podcast-icon'
import PremiumIcon from '../../../images/icons/premium-icon'
import ShopIcon from '../../../images/icons/shop-icon'
import profilePlaceholder from '../../../images/profile-placeholder.png'
import { authState } from '../../../state/auth-state'
import ActionSelector from '../../action-selector'
import CenteredModal from '../../modals/centered-modal'
import ConfirmationPrompt from '../../modals/confirmation-prompt'
import FullScreenModal from '../../full-screen-modal'
import IconAndLabelButton from '../../icon-and-label-button'
import LogoLink from '../../logo-link'
import { Avatar } from '../../user-avatar'
import NotificationBell from '../../notifications/notification-bell'
import BecomeImportantButton from '../become-important-button'
import StandardButton from '../../buttons/standard-button'
import LoadingShimmer from '../../loading-shimmer'
import { colors } from '../../../constants/colors'
import { navigateToProfile } from '../../../utilities/account'
import { openGleap } from '../../../utilities/gleap'

const HeaderDesktop = () => {
  const { account, accountLoading, logout } = authState()
  const theme = useTheme()
  const image = account?.user?.profile.public.avatar
  const userName = account?.user?.profile.public.name
  const premium = account?.premium_access

  const location = useLocation()

  // -------------------------------------------
  // search modal
  // -------------------------------------------

  const searchModalProps = useSearchModal()

  // -------------------------------------------
  // confirm logout modal
  // -------------------------------------------

  const [logoutModalOpen, setLogoutModalOpen] = useState(false)
  const confirmLogout = () => {
    logout()
    hideLogoutModal()
  }
  const hideLogoutModal = () => {
    setLogoutModalOpen(false)
  }

  return (
    <Box width="100%">
      <HBox gap={2} width="100%" alignItems="flex-start">
        <LogoLink includeINILink />
        <HBox gap={1} justifyContent="space-between" alignItems="flex-start">
          {location.pathname !== '/' && (
            <WhiteBox p={0.25} height={64}>
              <IconAndLabelButton
                color="secondary"
                label="Search"
                icon={<MagGlassIcon size={17} />}
                sx={{ height: '100%', p: 1.5, pb: 1.25 }}
                onClick={() => searchModalProps.setModalOpen(true)}
              />
            </WhiteBox>
          )}
          <WhiteBox p={0}>
            <HBox p={0.25} gap={0.25} height={64}>
              {/* <IconAndLabelButton
                color="secondary"
                label="Actions"
                icon={<LightningIcon size={17} />}
              /> */}
              <IconAndLabelButton
                color="secondary"
                label="Shop"
                icon={<ShopIcon size={17} />}
                component="a"
                href="https://shop.importantnotimportant.com/"
                target="_blank"
              />
              <IconAndLabelButton
                color="secondary"
                label="Podcast"
                icon={<PodcastIcon size={17} />}
                component={'a'}
                href="https://podcast.importantnotimportant.com/"
                target="_blank"
              />
              <IconAndLabelButton
                color="secondary"
                label="Get Updates"
                icon={<NewsletterIcon size={17} />}
                component={'a'}
                href="https://www.importantnotimportant.com/wcid-weekly-subscribe"
                target="_blank"
              />
              <IconAndLabelButton
                color="secondary"
                label="FAQ"
                icon={<FaqIcon size={17} />}
                component="a"
                href="/faq"
              />
            </HBox>
          </WhiteBox>
        </HBox>
        <HBox gap={1} flex={1} justifyContent="flex-end" height={64}>
          {accountLoading ? (
            <LoadingShimmer
              borderRadius="15px"
              width="200px"
              height="64px"
              customColors={
                theme.palette.mode === 'light'
                  ? { base: colors.BLACK_10, highlight: colors.WHITE }
                  : null
              }
              speed={2}
            />
          ) : (
            <>
              {!premium && <BecomeImportantButton />}
              <IconAndLabelButton
                color="secondary"
                label="Get Help"
                icon={<FaqIcon size={17} />}
                onClick={() => {
                  void openGleap()
                }}
              />
              <NotificationBell circular />
              {account && (
                <CircleButton
                  color="tertiary"
                  variant="contained"
                  disableElevation
                  aria-label={userName ? `${userName}'s profile` : 'Profile'}
                  onClick={() => {
                    navigateToProfile(account)
                  }}
                >
                  <Badge badgeContent={premium ? <PremiumIcon /> : 0}>
                    <Avatar
                      src={image || profilePlaceholder}
                      size={46}
                      style={{ borderRadius: '50%' }}
                    />
                  </Badge>
                </CircleButton>
              )}
              {account ? (
                <CircleButton
                  variant="contained"
                  color="tertiary"
                  disableElevation
                  aria-label="Logout"
                  onClick={() => setLogoutModalOpen(true)}
                >
                  <LogoutIcon />
                </CircleButton>
              ) : (
                <StandardButton
                  variant="text"
                  color="tertiary"
                  disableElevation
                  onClick={() => navigate('/login')}
                >
                  <Typography color="textPrimary" sx={{ padding: '0px' }} fontWeight={500}>
                    Log In
                  </Typography>
                </StandardButton>
              )}
            </>
          )}
        </HBox>
      </HBox>
      <CenteredModal open={logoutModalOpen} onClose={hideLogoutModal} positionAtBottomOnMobile>
        <ConfirmationPrompt
          onDeny={hideLogoutModal}
          onConfirm={confirmLogout}
          title={`Confirm Logout`}
          description="Are you sure you want to logout from WCID?"
          denyText="oops, no"
          confirmText="yes"
        />
      </CenteredModal>
      <FullScreenModal
        open={Boolean(searchModalProps.modalOpen)}
        size={MODAL_WIDTH_MEDIUM}
        onClose={() => searchModalProps.setModalOpen(false)}
      >
        <VBox p={2} flex={1}>
          <ActionSelector
            resultsOffset={2}
            sx={{ flex: 1 }}
            onSearch={() => searchModalProps.setModalOpen(false)}
          />
        </VBox>
      </FullScreenModal>
    </Box>
  )
}

export default HeaderDesktop
